import React, { useContext, useState } from "react";
import "../../styles/conversationalCallCard.css";
import ContentComponent from "../common/ContentComponent";
import callBadge from "../../images/call_image.png";
import { WebsiteContext } from "../../context/AppContext";
import { IoCall, IoCloseCircle } from "react-icons/io5";
import { HiSparkles, HiLightBulb, HiClock } from "react-icons/hi2";
import { LuRefreshCw } from "react-icons/lu";
import { FaUserFriends } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const samplePartners = [
  {
    name: "Sarah M.",
    initials: "SM",
    accent: "London, UK",
    level: "Intermediate",
    topic: "What is your dream travel destination and what would you do first once you arrive?",
    tip: "Use open-ended questions like 'Have you ever visited...?' to keep conversations natural.",
  },
  {
    name: "David R.",
    initials: "DR",
    accent: "Toronto, Canada",
    level: "Advance",
    topic: "Discuss the pros and cons of remote working versus traditional office collaboration.",
    tip: "Structure your thoughts using 'On one hand...' followed by 'On the other hand...'.",
  },
  {
    name: "Elena G.",
    initials: "EG",
    accent: "Madrid, Spain",
    level: "Beginner",
    topic: "Describe your favorite childhood memory and why it made a lasting impression.",
    tip: "Don't worry about perfection; native speakers prioritize clarity over complex grammar.",
  },
];

const ContentTwo = () => {
  const { isMobileView, dark } = useContext(WebsiteContext);

  const [selectedLevel, setSelectedLevel] = useState("Intermediate");
  const [selectedGender, setSelectedGender] = useState("Any");
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [isCallActive, setIsCallActive] = useState(false);
  const [partnerIndex, setPartnerIndex] = useState(0);

  const currentPartner = samplePartners[partnerIndex % samplePartners.length];

  const handleStartCall = () => {
    setIsCallActive(true);
  };

  const handleEndCall = () => {
    setIsCallActive(false);
  };

  const handleNextPartner = () => {
    setPartnerIndex((prev) => (prev + 1) % samplePartners.length);
  };

  const cardContent = (
    <div className="conversational_card_wrapper">
      <img src={callBadge} alt="3D Call Receiver" className="conversational_3d_badge" />

      <div className={`conversational_card ${dark ? "conversational_card_dark" : "conversational_card_light"}`}>
        {/* Top Bar */}
        <div className="convo_top_bar">
          <div className="convo_status_indicator">
            <span className="convo_pulsing_dot"></span>
            <span>1,420+ Co-Learners Online</span>
          </div>
          <span className="convo_session_badge">
            <HiClock size={13} /> 30 Min Free Call
          </span>
        </div>

        <AnimatePresence mode="wait">
          {!isCallActive ? (
            <motion.div
              key="setup"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              {/* Level Selector */}
              <div className="convo_filter_group">
                <div className="convo_filter_label">Select Co-Learner Level:</div>
                <div className="convo_pill_row">
                  {["Beginner", "Intermediate", "Advance"].map((lvl) => (
                    <button
                      key={lvl}
                      className={`convo_pill_btn ${selectedLevel === lvl ? "convo_pill_btn_active" : ""}`}
                      onClick={() => setSelectedLevel(lvl)}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Gender Preference */}
              <div className="convo_filter_group">
                <div className="convo_filter_label">Select Gender:</div>
                <div className="convo_pill_row">
                  {["Any", "Male", "Female"].map((g) => (
                    <button
                      key={g}
                      className={`convo_pill_btn ${selectedGender === g ? "convo_pill_btn_active" : ""}`}
                      onClick={() => setSelectedGender(g)}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {/* Language Preference */}
              <div className="convo_filter_group">
                <div className="convo_filter_label">Select Language:</div>
                <div className="convo_pill_row">
                  {["English", "Hindi", "Global"].map((lang) => (
                    <button
                      key={lang}
                      className={`convo_pill_btn ${selectedLanguage === lang ? "convo_pill_btn_active" : ""}`}
                      onClick={() => setSelectedLanguage(lang)}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              {/* Idle Action Box */}
              <div className="convo_idle_box">
                <div className="convo_notice_text">
                  <HiClock size={15} style={{ flexShrink: 0 }} />
                  <span>Talk for up to 30 minutes, then the call will end automatically.</span>
                </div>

                <button className="convo_call_btn" onClick={handleStartCall}>
                  <IoCall size={16} />
                  <span>Call Now • Connect with Co-Learner</span>
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="active-call"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25 }}
            >
              {/* Active Call Simulation */}
              <div className="convo_active_call_box">
                <div className="convo_partner_row">
                  <div className="convo_partner_info">
                    <div className="convo_partner_avatar">{currentPartner.initials}</div>
                    <div>
                      <div className="convo_partner_name">{currentPartner.name}</div>
                      <div className="convo_partner_sub">
                        {currentPartner.accent} • {selectedLevel}
                      </div>
                    </div>
                  </div>
                  <span className="convo_timer_badge">
                    <HiClock size={13} /> 28:45 Left
                  </span>
                </div>

                {/* Animated Audio Waveform */}
                <div className="convo_sound_waves">
                  <div className="convo_wave_bar"></div>
                  <div className="convo_wave_bar"></div>
                  <div className="convo_wave_bar"></div>
                  <div className="convo_wave_bar"></div>
                  <div className="convo_wave_bar"></div>
                </div>

                {/* Suggested Conversation Topic */}
                <div className="convo_topic_box">
                  <div className="convo_topic_title">Current Dialogue Prompt:</div>
                  <div className="convo_topic_text">"{currentPartner.topic}"</div>
                </div>

                {/* Confidence Pro Tip */}
                <div className="convo_tip_box">
                  <HiLightBulb size={15} style={{ flexShrink: 0 }} />
                  <span>{currentPartner.tip}</span>
                </div>

                {/* Controls */}
                <div className="convo_actions_row">
                  <button className="convo_switch_btn" onClick={handleNextPartner} title="Switch Partner">
                    <LuRefreshCw size={13} /> Next Partner
                  </button>
                  <button className="convo_end_btn" onClick={handleEndCall}>
                    <IoCloseCircle size={15} /> End Practice Call
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer Bar */}
        <div className="convo_footer_bar">
          <span className="convo_hint">
            <FaUserFriends size={12} style={{ marginRight: 4 }} /> Peer-to-peer audio • Moderated
          </span>
          <button
            className="convo_mode_btn"
            onClick={() => (isCallActive ? handleEndCall() : handleStartCall())}
            title="Toggle simulator"
          >
            <HiSparkles size={12} /> {isCallActive ? "Back to Setup" : "Quick Demo"}
          </button>
        </div>
      </div>
    </div>
  );

  const textContent = (
    <div className="conversational_text_wrapper">
      <ContentComponent
        title={"Mastering Conversational English: Confident Speaking with New Voices"}
        content={
          "Embrace the challenge of speaking with new people and refine your communication abilities through engaging dialogues and thoughtful tips."
        }
      />
    </div>
  );

  return (
    <div className={isMobileView ? "conversational_section_mobile" : "conversational_section"}>
      {isMobileView ? (
        <>
          {textContent}
          {cardContent}
        </>
      ) : (
        <>
          {cardContent}
          {textContent}
        </>
      )}
    </div>
  );
};

export default ContentTwo;
